import { useMemo } from 'react';

import { CampaignSubSource } from 'utils/constants/campaign';

import { Source } from 'store/models/createCampaign';

interface UseSourceItemStateParams {
    source: Source;
    adSource: CampaignSubSource[];
    savedSources: CampaignSubSource[];
    isEditAdvertising: boolean;
}

interface UseSourceItemStateResult {
    isSourceDisabled: boolean;
    isSourceChecked: boolean;
    isSourceIndeterminate: boolean;
    getSubSourceState: (
        subSource: CampaignSubSource,
        enabled: boolean
    ) => {
        checked: boolean;
        disabled: boolean;
    };
}

export const useSourceItemState = ({
    source,
    adSource,
    savedSources,
    isEditAdvertising,
}: UseSourceItemStateParams): UseSourceItemStateResult => {
    const { subSources } = source;

    const isEnabledSource = useMemo(() => subSources.some(({ enabled }) => enabled), [subSources]);

    const isAllSubSourcesSelected = useMemo(
        () => subSources.every(({ subSource }) => adSource.includes(subSource)),
        [subSources, adSource]
    );

    const isSomeSubSourcesSelected = useMemo(
        () => subSources.some(({ subSource }) => adSource.includes(subSource)),
        [subSources, adSource]
    );

    const isSourceIndeterminate = isSomeSubSourcesSelected && !isAllSubSourcesSelected;

    const isSomeSubSourcesSaved = useMemo(
        () => subSources.some(({ subSource }) => savedSources.includes(subSource)),
        [subSources, savedSources]
    );

    const isDisabledForAdvertising =
        isEditAdvertising && !isAllSubSourcesSelected && !isSourceIndeterminate && !isSomeSubSourcesSaved;

    const isSourceDisabled = !isEnabledSource || isDisabledForAdvertising;
    const isSourceChecked = isAllSubSourcesSelected;

    const getSubSourceState = useMemo(
        () => (subSource: CampaignSubSource, enabled: boolean) => ({
            checked: adSource.includes(subSource),
            disabled: !enabled || isDisabledForAdvertising,
        }),
        [adSource, isDisabledForAdvertising]
    );

    return {
        isSourceDisabled,
        isSourceChecked,
        isSourceIndeterminate,
        getSubSourceState,
    };
};
